import { z } from 'zod';
import type { HleModelId, HleModelUsage } from '../hle.js';

// Re-exported so consumers (the web app) get the whole HLE wire vocabulary from one module.
export type { HleModelId, HleModelUsage } from '../hle.js';

/** A language's stored source config + last-run status (project-admin panel). */
export interface HleSourceSummary {
  languageCode: string;
  languageName: string;
  enabled: boolean;
  spreadsheetId: string | null;
  sheetTab: string | null;
  lastRunAt: string | null;
  lastError: string | null;
  lastResult: HleRunSummary | null;
  /** A run is in flight right now (runs are detached — the POST returns immediately). */
  running: boolean;
  /** ISO time the in-flight run started; null when idle. */
  runStartedAt: string | null;
  /** Live counters of the in-flight run, flushed to the DB as it advances. */
  runProgress: HleRunSummary | null;
  /** Unattended testing: start a run for this language on a fixed cadence. */
  autoRun: boolean;
  autoRunIntervalMinutes: number;
  lastAutoRunAt: string | null;
}

/** Live progress computed from the sheet. */
export interface HleProgress {
  languageCode: string;
  readyItems: number;          // rows marked READY_FOR_TEST
  fullyTested: number;         // ready rows with all 5 O–S cells filled
  remainingCells: number;      // empty O–S cells among ready rows
  rowErrors: number;           // ready rows that fail to parse
  /** Header columns the sheet is missing — a non-empty `missingInput` blocks the run. */
  missingInput: string[];
  missingOutput: HleModelId[];
  /**
   * Why the sheet could not be read at all (not shared with the service account, bad
   * id, tab renamed). Reported rather than thrown so the dashboard can show the
   * language and say what to fix, instead of collapsing into a page-level error.
   */
  sheetError: string | null;
}

/** Result of one run, persisted on the source + returned to the caller. */
export interface HleRunSummary {
  languageCode: string;
  ranAt: string;
  /** false once the run has failed; a still-running summary reports `ok: true`. */
  ok: boolean;
  error: string | null;
  /** true while the run is still in flight (progress snapshot rather than a final result). */
  inProgress?: boolean;
  itemsConsidered: number;
  itemsDone: number;
  cellsWritten: number;
  cellsSkipped: number;
  rowErrors: number;
  perModelErrors: Record<HleModelId, number>;
  /** Tokens actually billed per model — the hard number behind the cost estimate. */
  perModelUsage: Record<HleModelId, HleModelUsage>;
  /** USD across models that have a configured price. */
  estimatedCostUsd: number;
  /** Models that ran but have no price configured, so are excluded from the estimate. */
  unpricedModels: HleModelId[];
  /** Models skipped entirely for want of an API key. */
  missingKeys: HleModelId[];
  /** Params a provider rejected and the engine stopped sending (e.g. "opus: sampling"). */
  narrowed: string[];
}

/**
 * One READY_FOR_TEST row, as the test runner lists it.
 *
 * Carries ONLY what the engine itself reads — item id, answer type, and the
 * local-language question. Gold (ANSWER, ANSWER_KEY, CHALLENGE_DESCRIPTION and their
 * translations) is never put on this wire, the same firewall the model calls observe.
 */
export interface HleItemRow {
  /** 1-based sheet row — the addressing unit a scoped run takes. */
  row: number;
  itemId: string;
  answerType: string;
  question: string;
  /** Models whose output column already holds an answer for this row. */
  filled: HleModelId[];
  /** Why the row cannot be run (bad ANSWER_TYPE, empty QUESTION, unknown code). */
  error: string | null;
}

/** The ready rows of one language's sheet, plus the same health facts as progress. */
export interface HleItemsResponse {
  languageCode: string;
  tab: string;
  items: HleItemRow[];
  missingInput: string[];
  missingOutput: HleModelId[];
  sheetError: string | null;
}

/** One model's answer to the pre-flight probe — cheap key/model-id/param validation. */
export interface HleProbeResult {
  model: HleModelId;
  modelId: string;
  ok: boolean;
  error: string | null;
  ms: number;
  tokensIn: number | null;
  tokensOut: number | null;
  /** Excerpt of the reply, so a nonsense-but-200 response is visible. */
  sample: string | null;
}

export interface HleProbeReport {
  results: HleProbeResult[];
  missingKeys: HleModelId[];
}

/** Cadences offered for unattended testing (minutes). */
export const HLE_AUTO_RUN_INTERVALS = [15, 30, 60, 180, 360, 720, 1440] as const;

export const hleSourceUpdateSchema = z.object({
  spreadsheetId: z.string().max(200).optional(),
  sheetTab: z.string().max(200).optional(),
  enabled: z.boolean().optional(),
  autoRun: z.boolean().optional(),
  autoRunIntervalMinutes: z.number().int().min(15).max(1440).optional(),
});
export type HleSourceUpdate = z.infer<typeof hleSourceUpdateSchema>;

export const hleRunRequestSchema = z.object({
  languageCode: z.string().min(1),
  force: z.boolean().default(false),
  /** Sheet rows to run. Omitted/empty = every ready row with an unfilled cell. */
  rows: z.array(z.number().int().positive()).max(5000).optional(),
});
export type HleRunRequest = z.infer<typeof hleRunRequestSchema>;

/** `POST /hle/run` is fire-and-forget: it validates, starts, and returns immediately. */
export interface HleRunAccepted {
  started: boolean;
  languageCode: string;
  /** Why a run was not started (already running, missing headers, no spreadsheet). */
  message: string | null;
}
